import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Btn, DangerBtn, Loading, Msg, Section, Select, TextInput, panelGet, useAdminGet } from '../kit';
import { Drawer } from '../email/emailKit';
import { RunTable } from './Runs';
import { UniversityChangeHeatmap } from './Heatmap';
import {
  Collapse, Dot, EVENT_SERIES, Fact, Facts, HEALTH_LEVEL, JsonBox, MOVED_REJECT_LABEL, MOVED_SOURCE_LABEL, MovedCandidates,
  MODE_LABEL, NoteLine, NumInput, Pill, ProbeBadge, type RecrawlCtx, RunLink, S, StackedColumns, StatusPill, Switch, Tile, WEEKDAYS,
  arr, bucketLabel, fmtAgo, fmtBytes, fmtIST, fmtN, fmtPct, movedDetectionText, movedNotApplied, n0, num, obj, scheduleText,
  queueText, scrapeResults, str, toProbe, toUni, useAction,
} from './shared';
import { RUN_MODE_INFO, RUN_MODES, type RunMode, RunStartModal } from './RunStart';
import { ProjectionBox, fullRunApprovalText, useAutoApprove } from './IfApproved';

/**
 * One university, everything about it (GET university {code}): the manifest
 * (recrawl/manifests/<CODE>.json) and what the admin overrides on top of it,
 * the throttle the scripts declare next to the one the governor enforces, its
 * schedule, request health, change timeline and runs — and the controls:
 * Probe, Dry run, Full run (each through a confirmation that says what happens; disabled with the
 * reason while a run is open, the university is switched off, or a manual-file university has no
 * file — then the upload is offered), Pause, "If approved" (the website and master counts now →
 * after approving its latest results), the schedule / throttle / guard / cleanup /
 * moved-detection editors (POST university_set; a null value removes an override, so "Reset"
 * goes back to the manifest) and, for a manual-file university, the source
 * upload (POST upload_source).
 */

const MAX_UPLOAD_MB = 40;

/** Throttle fields and the bounds engine._THROTTLE_LIMITS enforces — the same as manifest.THROTTLE_BOUNDS, so a
 *  saved value is the value the governor runs with (a wider range here was saved, then clamped without a word). */
const THROTTLE_FIELDS: { key: string; label: string; min: number; max: number; step: number; unit?: string; help: string }[] = [
  { key: 'max_workers', label: 'Max workers', min: 1, max: 32, step: 1, help: 'Thread-pool size the governor allows (the scripts’ own pools are capped to it)' },
  { key: 'min_interval_s', label: 'Min interval per host', min: 0, max: 30, step: 0.05, unit: 's', help: 'Minimum gap between two requests to the same host' },
  { key: 'timeout_s', label: 'Request timeout', min: 5, max: 600, step: 1, unit: 's', help: 'Default timeout for a request that sets none' },
  { key: 'max_retries', label: 'Retries', min: 0, max: 10, step: 1, help: 'Retries with backoff on 429 / 503 (Retry-After honoured)' },
];

const CLEANUP_BOOLS: { key: string; label: string; help: string }[] = [
  { key: 'restore_distinct_ref_dups', label: 'Restore distinct reference duplicates', help: 'Keep records that share a reference number but have a different title' },
  { key: 'remove_no_ref', label: 'Remove records without a reference number', help: 'Drops every record whose reference number is empty' },
  { key: 'remove_blank_fields', label: 'Remove records with blank fields', help: 'Drops records missing the reference number, full text, URL or title' },
  { key: 'remove_short_text', label: 'Remove records with very short text', help: 'Drops records whose full text is missing or under 100 characters' },
];

const valid = (v: string, min: number, max: number, int = false) => {
  if (v.trim() === '') return false;
  const n = Number(v);
  return Number.isFinite(n) && n >= min && n <= max && (!int || Number.isInteger(n));
};

// ---------------------------------------------------------------------------
// editors
// ---------------------------------------------------------------------------
const ScheduleEditor: React.FC<{ code: string; uni: any; manifest: any; overrides: any; onSaved: () => void }> =
  ({ code, uni, manifest, overrides, onSaved }) => {
  const sc = obj(uni.schedule);
  const [enabled, setEnabled] = useState(!!sc.enabled);
  const [every, setEvery] = useState(str(sc.every_days ?? 30));
  const [weekday, setWeekday] = useState(num(sc.weekday) === null ? '' : String(sc.weekday));
  const [hour, setHour] = useState(num(sc.hour_ist) === null ? '' : String(sc.hour_ist));
  const act = useAction(onSaved);
  const okEvery = valid(every, 1, 365, true);
  const dirty = enabled !== !!sc.enabled || every !== str(sc.every_days ?? 30)
    || weekday !== (num(sc.weekday) === null ? '' : String(sc.weekday)) || hour !== (num(sc.hour_ist) === null ? '' : String(sc.hour_ist));
  const save = () => act.post('save', 'university_set', {
    code, overrides: { schedule: { enabled, every_days: Number(every), weekday: weekday === '' ? null : Number(weekday), hour_ist: hour === '' ? null : Number(hour) } },
  }, () => 'Schedule saved.');
  const ms = obj(manifest.schedule);
  return (
    <Section title="Schedule"
      right={obj(overrides).schedule ? <Pill tone="navy" title="Overrides the manifest's schedule">overridden</Pill> : <Pill tone="grey">manifest default</Pill>}>
      <div className="flex items-center gap-3 flex-wrap text-sm">
        <label className="inline-flex items-center gap-2">
          <Switch on={enabled} onChange={setEnabled} label="Schedule on" /> {enabled ? 'On' : 'Off'}
        </label>
        <label className="inline-flex items-center gap-1.5">every <NumInput value={every} onChange={setEvery} min={1} max={365} width="w-20" invalid={!okEvery} /> days</label>
        <label className="inline-flex items-center gap-1.5">on
          <Select value={weekday} onChange={setWeekday} options={['', '0', '1', '2', '3', '4', '5', '6']}
            labels={{ '': 'any weekday', ...Object.fromEntries(WEEKDAYS.map((w, i) => [String(i), w])) }} />
        </label>
        <label className="inline-flex items-center gap-1.5">not before
          <Select value={hour} onChange={setHour} options={['', ...Array.from({ length: 24 }, (_, i) => String(i))]}
            labels={{ '': 'any hour', ...Object.fromEntries(Array.from({ length: 24 }, (_, i) => [String(i), `${String(i).padStart(2, '0')}:00 IST`])) }} />
        </label>
      </div>
      <div className="text-xs text-grayscale-1100">
        Next due is the day of (last successful run + every N days), moved to the weekday, at the hour (IST); a failed run is retried after
        the retry delay in Settings. Scheduled starts also need the global switch on and RECRAWL_AUTO=1 on the server, and never fall inside the
        quiet hours. Manifest default: {scheduleText(ms)}.
      </div>
      <div className="text-xs text-grayscale-1200">
        Now: <b>{scheduleText(sc)}</b> · next due <b>{uni.next_due_at ? fmtIST(uni.next_due_at) : '—'}</b>
        {uni.due_reason && <span className="text-grayscale-1100"> — {str(uni.due_reason)}</span>}
      </div>
      <div className="flex gap-2 flex-wrap">
        <Btn kind="primary" small onClick={save} disabled={!dirty || !okEvery || !!act.busy}>{act.busy === 'save' ? 'Saving…' : 'Save schedule'}</Btn>
        {obj(overrides).schedule && (
          <Btn small disabled={!!act.busy} onClick={() => act.post('reset', 'university_set', { code, overrides: { schedule: null } }, () => 'Back to the manifest schedule.')}>
            Reset to manifest
          </Btn>
        )}
      </div>
      <NoteLine note={act.note} />
    </Section>
  );
};

const ThrottleEditor: React.FC<{ code: string; data: any; health: any; onSaved: () => void }> = ({ code, data, health, onSaved }) => {
  const eff = obj(obj(data.throttle).effective);
  const manDefault = obj(obj(obj(data.manifest).throttle).default);
  const ovThrottle = obj(obj(obj(data.overrides).throttle).default);
  const declared = arr(obj(data.throttle).declared);
  const rec = obj(obj(health).recommendation);
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(THROTTLE_FIELDS.map(f => [f.key, num(eff[f.key]) === null ? '' : String(eff[f.key])])));
  const act = useAction(onSaved);
  const changed = THROTTLE_FIELDS.filter(f => draft[f.key] !== '' && draft[f.key] !== (num(eff[f.key]) === null ? '' : String(eff[f.key])));
  const bad = THROTTLE_FIELDS.filter(f => draft[f.key] !== '' && !valid(draft[f.key], f.min, f.max, f.step === 1));
  const save = () => act.post('save', 'university_set', {
    code, overrides: { throttle: { default: Object.fromEntries(changed.map(f => [f.key, Number(draft[f.key])])) } },
  }, () => 'Throttle saved — the next run uses it.');
  const suggest = obj(rec.suggest);
  const level = str(rec.level) || 'none';
  const byKind = useMemo(() => {
    const m = new Map<string, any[]>();
    declared.forEach(d => { const k = str(obj(d).kind) || 'other'; m.set(k, [...(m.get(k) || []), d]); });
    return Array.from(m.entries());
  }, [declared]);
  return (
    <Section title="Throttle — declared by the scripts vs enforced by the governor"
      right={Object.keys(ovThrottle).length ? <Pill tone="navy">overridden: {Object.keys(ovThrottle).join(', ')}</Pill> : <Pill tone="grey">manifest default</Pill>}>
      <div className="text-xs text-grayscale-1100">
        The scripts carry the parallelism and sleeps their author found safe when they were written; those numbers are never trusted at run
        time. The governor caps every thread pool, spaces requests per host and backs off on 429 / 503 using the values below.
      </div>
      {level !== 'none' || rec.text ? (
        <div className={`rounded-lg border px-3 py-2 text-[13px] flex flex-col gap-1 ${level === 'bad' ? 'border-red-200 bg-red-50' : level === 'warn' ? 'border-amber-300 bg-amber-50' : 'border-green-200 bg-green-50'}`}>
          <div className="flex items-center gap-2 flex-wrap">
            <Dot tone={HEALTH_LEVEL[level]?.tone || 'grey'} /> <b>Recommendation</b> <span>{str(rec.text) || '—'}</span>
            {Object.keys(suggest).length > 0 && (
              <Btn small onClick={() => setDraft(d => ({ ...d, ...Object.fromEntries(Object.entries(suggest).map(([k, v]) => [k, String(v)])) }))}>
                Fill in the suggestion
              </Btn>
            )}
          </div>
          {arr(rec.notes).map((n, i) => <div key={i} className="text-xs text-grayscale-1200">{str(n)}</div>)}
        </div>
      ) : null}
      <div className="overflow-auto rounded-lg border border-grayscale-300">
        <table className="w-full text-sm">
          <thead className="bg-grayscale-200 text-xs text-grayscale-1200">
            <tr><th className="text-left px-3 py-1.5">Setting</th><th className="text-left px-3 py-1.5">Manifest</th>
              <th className="text-left px-3 py-1.5">Effective</th><th className="text-left px-3 py-1.5">New value</th><th /></tr>
          </thead>
          <tbody>
            {THROTTLE_FIELDS.map(f => {
              const isOv = ovThrottle[f.key] !== undefined;
              const isBad = bad.includes(f);
              return (
                <tr key={f.key} className="border-t border-grayscale-200">
                  <td className="px-3 py-1.5" title={f.help}>{f.label}<div className="text-[10.5px] text-grayscale-1100">{f.min}–{f.max}{f.unit ? ` ${f.unit}` : ''}</div></td>
                  <td className="px-3 py-1.5 text-grayscale-1100">{num(manDefault[f.key]) === null ? '—' : `${manDefault[f.key]}${f.unit ? ` ${f.unit}` : ''}`}</td>
                  <td className="px-3 py-1.5"><b>{num(eff[f.key]) === null ? '—' : `${eff[f.key]}${f.unit ? ` ${f.unit}` : ''}`}</b>{isOv && <span className="text-[10px] text-secondary-500"> override</span>}</td>
                  <td className="px-3 py-1.5">
                    <NumInput value={draft[f.key]} onChange={v => setDraft(d => ({ ...d, [f.key]: v }))} min={f.min} max={f.max} step={f.step} invalid={isBad} />
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {isOv && (
                      <button type="button" className="text-xs text-primary-700 underline underline-offset-2" disabled={!!act.busy}
                        onClick={() => act.post('reset1', 'university_set', { code, overrides: { throttle: { default: { [f.key]: null } } } }, () => `${f.label} back to the manifest value.`)}>
                        reset
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2 flex-wrap items-center">
        <Btn kind="primary" small onClick={save} disabled={!changed.length || bad.length > 0 || !!act.busy}>
          {act.busy === 'save' ? 'Saving…' : `Save throttle${changed.length ? ` (${changed.length} change${changed.length === 1 ? '' : 's'})` : ''}`}
        </Btn>
        {Object.keys(ovThrottle).length > 0 && (
          <Btn small disabled={!!act.busy} onClick={() => act.post('reset', 'university_set', { code, overrides: { throttle: null } }, () => 'Throttle back to the manifest defaults.')}>
            Reset all to manifest
          </Btn>
        )}
        {bad.length > 0 && <span className="text-xs text-red-700">Out of bounds: {bad.map(f => f.label).join(', ')}</span>}
      </div>
      <NoteLine note={act.note} />
      <Collapse title={`What the scripts declare (${declared.length} finding${declared.length === 1 ? '' : 's'} from the static scan)`} open={declared.length > 0 && declared.length <= 12}>
        {!declared.length ? <div className="text-xs text-grayscale-1100">The scan found no thread pools, sleeps or timeouts.</div> : (
          <div className="flex flex-col gap-2">
            {byKind.map(([kind, items]) => (
              <div key={kind} className="flex flex-col gap-0.5">
                <div className="text-[11px] uppercase tracking-wide text-grayscale-1100">{kind}</div>
                {items.map((d, i) => (
                  <div key={i} className="flex items-baseline gap-2 text-[12px]">
                    <code className="font-mono text-grayscale-1100 shrink-0">{str(d.file)}:{str(d.line)}</code>
                    <code className="font-mono text-grayscale-1300 truncate" title={str(d.what)}>{str(d.what)}</code>
                    <b className="ml-auto shrink-0">{str(d.value)}</b>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </Collapse>
    </Section>
  );
};

const GuardEditor: React.FC<{ code: string; data: any; onSaved: () => void }> = ({ code, data, onSaved }) => {
  const eff = obj(data.effective);
  const ov = obj(data.overrides);
  const [enabled, setEnabled] = useState(eff.enabled !== false);
  const [always, setAlways] = useState(!!eff.always_approve);
  const [ratio, setRatio] = useState(num(eff.expected_min_ratio) === null ? '' : String(eff.expected_min_ratio));
  const [misses, setMisses] = useState(num(ov.remove_after_misses) === null ? '' : String(ov.remove_after_misses));
  const [note, setNote] = useState(str(ov.note));
  const act = useAction(onSaved);
  const okRatio = ratio === '' || valid(ratio, 0.05, 1);
  const okMiss = misses === '' || valid(misses, 1, 10, true);
  const body: Record<string, any> = {};
  if (enabled !== (eff.enabled !== false)) body.enabled = enabled;
  if (always !== !!eff.always_approve) body.always_approve = always;
  if (ratio !== (num(eff.expected_min_ratio) === null ? '' : String(eff.expected_min_ratio))) body.expected_min_ratio = ratio === '' ? null : Number(ratio);
  if (misses !== (num(ov.remove_after_misses) === null ? '' : String(ov.remove_after_misses))) body.remove_after_misses = misses === '' ? null : Number(misses);
  if (note !== str(ov.note)) body.note = note.trim() === '' ? null : note;
  const dirty = Object.keys(body).length > 0;
  return (
    <Section title="Guard, approval and notes">
      <div className="grid gap-3 sm:grid-cols-2 text-sm">
        <label className="flex items-center gap-2" title="A disabled university is never scheduled">
          <Switch on={enabled} onChange={setEnabled} /> University enabled
        </label>
        <label className="flex items-center gap-2" title="Every run of this university waits for an admin, even with auto-approve on">
          <Switch on={always} onChange={setAlways} /> Always needs approval
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Held when mapped / master active is below (0.05–1)
          <NumInput value={ratio} onChange={setRatio} min={0.05} max={1} step={0.05} invalid={!okRatio} placeholder="setting" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Missing runs in a row before a removal is proposed (1–10; empty = global setting)
          <NumInput value={misses} onChange={setMisses} min={1} max={10} invalid={!okMiss} placeholder="setting" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100 sm:col-span-2">
          Note (shown in the table; up to 500 characters)
          <TextInput value={note} onChange={setNote} wide placeholder="e.g. waiting for the TTO to fix their portal" />
        </label>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Btn kind="primary" small disabled={!dirty || !okRatio || !okMiss || !!act.busy}
          onClick={() => act.post('save', 'university_set', { code, overrides: body }, () => 'Saved.')}>{act.busy === 'save' ? 'Saving…' : 'Save'}</Btn>
      </div>
      <NoteLine note={act.note} />
    </Section>
  );
};

const CleanupEditor: React.FC<{ code: string; data: any; onSaved: () => void }> = ({ code, data, onSaved }) => {
  const eff = obj(obj(data.effective).cleanup);
  const man = obj(obj(data.manifest).cleanup);
  const ov = obj(obj(data.overrides).cleanup);
  const [d, setD] = useState<Record<string, any>>(() => ({
    ...Object.fromEntries(CLEANUP_BOOLS.map(b => [b.key, !!eff[b.key]])),
    drop_ref_prefix: str(eff.drop_ref_prefix), drop_title_prefix: str(eff.drop_title_prefix),
  }));
  const act = useAction(onSaved);
  const body: Record<string, any> = {};
  CLEANUP_BOOLS.forEach(b => { if (d[b.key] !== !!eff[b.key]) body[b.key] = d[b.key]; });
  (['drop_ref_prefix', 'drop_title_prefix'] as const).forEach(k => { if (d[k] !== str(eff[k])) body[k] = d[k] === '' ? null : d[k]; });
  const dirty = Object.keys(body).length > 0;
  return (
    <Section title="Cleanup flags"
      right={Object.keys(ov).length ? <Pill tone="navy">overridden: {Object.keys(ov).join(', ')}</Pill> : <Pill tone="grey">manifest default</Pill>}>
      <div className="text-xs text-grayscale-1100">
        Applied by the cleanup step after the scrape; the run reports what each one dropped.
      </div>
      <div className="grid gap-2 sm:grid-cols-2 text-sm">
        {CLEANUP_BOOLS.map(b => (
          <label key={b.key} className="flex items-center gap-2" title={b.help}>
            <Switch on={!!d[b.key]} onChange={v => setD(x => ({ ...x, [b.key]: v }))} />
            <span>{b.label}{!!man[b.key] !== !!d[b.key] && <span className="text-[10px] text-secondary-500"> (manifest: {man[b.key] ? 'on' : 'off'})</span>}</span>
          </label>
        ))}
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">Drop records whose reference starts with
          <TextInput value={str(d.drop_ref_prefix)} onChange={v => setD(x => ({ ...x, drop_ref_prefix: v }))} placeholder="none" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">Drop records whose title starts with
          <TextInput value={str(d.drop_title_prefix)} onChange={v => setD(x => ({ ...x, drop_title_prefix: v }))} placeholder="none" />
        </label>
      </div>
      <div className="flex gap-2 flex-wrap">
        <Btn kind="primary" small disabled={!dirty || !!act.busy}
          onClick={() => act.post('save', 'university_set', { code, overrides: { cleanup: body } }, () => 'Cleanup flags saved.')}>
          {act.busy === 'save' ? 'Saving…' : 'Save cleanup flags'}
        </Btn>
        {Object.keys(ov).length > 0 && (
          <Btn small disabled={!!act.busy} onClick={() => act.post('reset', 'university_set', { code, overrides: { cleanup: null } }, () => 'Cleanup flags back to the manifest.')}>
            Reset to manifest
          </Btn>
        )}
      </div>
      <NoteLine note={act.note} />
    </Section>
  );
};

/** manifest.MOVED_OFF_RISKS: moved detection is off for these unless an override (or the manifest) turns it on. */
const MOVED_OFF_RISKS = ['no_reference_numbers', 'unstable_keys'];
const MOVED_MIN_SIM = 0.5;

/** The moved-technologies switch and similarity floor of one university (overrides.moved =
 *  {enabled, min_similarity}), what the worker will use (effective._moved from
 *  manifest.moved_config) and what the last diff paired or rejected. */
const MovedEditor: React.FC<{ code: string; data: any; ctx: RecrawlCtx; onSaved: () => void }> = ({ code, data, ctx, onSaved }) => {
  const m = obj(data.manifest);
  const effAll = obj(data.effective);
  const ov = obj(obj(data.overrides).moved);
  const man = obj(m.moved);
  const em = obj(effAll._moved);
  const hasEff = typeof em.enabled === 'boolean';
  const initEnabled = ov.enabled === true ? 'on' : ov.enabled === false ? 'off' : '';
  const initSim = num(ov.min_similarity) === null ? '' : String(ov.min_similarity);
  const [enabled, setEnabled] = useState(initEnabled);
  const [sim, setSim] = useState(initSim);
  const act = useAction(onSaved);
  const okSim = sim.trim() === '' || valid(sim, 0, 1);
  const body: Record<string, any> = {};
  if (enabled !== initEnabled) body.enabled = enabled === '' ? null : enabled === 'on';
  if (sim !== initSim) body.min_similarity = sim.trim() === '' ? null : Number(sim);
  const dirty = Object.keys(body).length > 0;

  const risk = (arr(effAll.risk).length ? arr(effAll.risk) : arr(m.risk)).map(str);
  const riskOff = str(effAll.kind || m.kind) === 'manual_file' || risk.some(r => MOVED_OFF_RISKS.includes(r));
  const manEnabled = typeof man.enabled === 'boolean' ? man.enabled : null;
  const inheritText = manEnabled !== null ? `manifest: ${manEnabled ? 'on' : 'off'}`
    : riskOff ? 'off: manual file or risk flag' : 'the global setting';
  const manSim = num(man.min_similarity);
  const effSim = num(em.min_similarity);
  const draftSim = sim.trim() === '' ? (manSim ?? MOVED_MIN_SIM) : Number(sim);
  const lowSim = Number.isFinite(draftSim) && draftSim < MOVED_MIN_SIM;

  // The last run that diffed: its moved counts, and the diff step's matcher stats (rejected candidates).
  const runs = arr(data.runs);
  const lastDiff = runs.find(r => num(obj(obj(r).counts).moved) !== null) || runs.find(r => num(obj(obj(r).counts).new) !== null);
  const lc = obj(obj(lastDiff).counts);
  const rid = lastDiff ? str(obj(lastDiff).id) : '';
  const [stats, setStats] = useState<Record<string, any> | null>(null);
  useEffect(() => {
    setStats(null);
    if (!rid) return undefined;
    let alive = true;
    panelGet(S, 'run', { id: rid }).then(({ data: rd, error }) => {
      if (!alive || error) return;
      const step = arr(obj(rd).steps).slice().reverse()
        .find(x => str(obj(x).step_id) === 'diff' && Object.keys(obj(obj(obj(x).counts).stats)).length > 0);
      setStats(obj(obj(obj(step).counts).stats));
    });
    return () => { alive = false; };
  }, [rid]);
  const st = obj(stats);
  const rejected = Object.entries(obj(st.moved_rejected)).filter(([, v]) => n0(v) > 0).sort((a, b) => n0(b[1]) - n0(a[1]));
  const notApplied = movedNotApplied(st);

  return (
    <Section title="Moved technologies (URL changes)"
      right={Object.keys(ov).length ? <Pill tone="navy">overridden: {Object.keys(ov).join(', ')}</Pill>
        : <Pill tone="grey">{Object.keys(man).length ? 'manifest' : 'default'}</Pill>}>
      <div className="text-xs text-grayscale-1100">
        A vanished URL is paired with a new URL of this university when the reference number is unique on both sides and the title or
        text agrees: the technology keeps its UTECH id, links, saved items and page, and the live row's URL is updated in place.
        Off = a changed URL is one removal plus one addition.
      </div>
      <div className="text-[12.5px] text-grayscale-1300">
        {hasEff ? (
          <>
            Now: <b>{em.enabled ? 'on' : 'off'}</b> ({MOVED_SOURCE_LABEL[str(em.source)] || str(em.source) || '—'})
            {' '}· minimum similarity <b>{effSim === null ? MOVED_MIN_SIM : effSim}</b>
            {effSim !== null && effSim < MOVED_MIN_SIM && (
              <span className="text-amber-800"> (pairs below {MOVED_MIN_SIM} are relaxed and always need approval)</span>
            )}
            {effSim !== null && effSim > MOVED_MIN_SIM && (
              <span className="text-grayscale-1100"> (stricter than the global {MOVED_MIN_SIM}: pairs below it are not made)</span>
            )}
            {!em.enabled && em.report_only && <span className="text-grayscale-1100"> · runs still report how many moves they would make</span>}
            {!em.enabled && str(em.source) === 'risk' && (
              <span className="text-grayscale-1100"> · a manual file or a no_reference_numbers / unstable_keys flag; On opts in</span>
            )}
          </>
        ) : <span className="text-grayscale-1100">The API does not report the effective moved settings yet.</span>}
      </div>
      {Object.keys(man).length > 0 && (
        <div className="text-[11.5px] text-grayscale-1100">
          Manifest: {manEnabled !== null ? `detection ${manEnabled ? 'on' : 'off'}` : 'detection inherited'}
          {manSim !== null ? ` · minimum similarity ${manSim}` : ''}.
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2 text-sm">
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Moved detection
          <Select value={enabled} onChange={setEnabled} options={['', 'on', 'off']}
            labels={{ '': `Inherit (${inheritText})`, on: 'On (override)', off: 'Off (override)' }} />
        </label>
        <label className="flex flex-col gap-1 text-xs text-grayscale-1100">
          Minimum title/text similarity (0–1; blank = {manSim !== null ? `the manifest's ${manSim}` : MOVED_MIN_SIM})
          <NumInput value={sim} onChange={setSim} min={0} max={1} step={0.05} invalid={!okSim}
            placeholder={String(manSim ?? MOVED_MIN_SIM)} />
          <span className="text-[11px]">
            Lower trusts the reference number; any pair below 0.5 always needs approval. Do not lower for universities that reuse numbers (Pitt).
            Higher than 0.5 is stricter: a pair below it stays one addition plus one removal.
          </span>
        </label>
      </div>
      {lowSim && okSim && (
        <div className="text-[12px] rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-amber-900">
          Below {MOVED_MIN_SIM}: a pair the global floor would reject is accepted on the reference number alone (relaxed), and a run with
          one always waits for an admin.
        </div>
      )}
      <div className="flex gap-2 flex-wrap">
        <Btn kind="primary" small disabled={!dirty || !okSim || !!act.busy}
          onClick={() => act.post('save', 'university_set', { code, overrides: { moved: body } }, () => 'Moved detection saved — the next run uses it.')}>
          {act.busy === 'save' ? 'Saving…' : 'Save'}
        </Btn>
        {Object.keys(ov).length > 0 && (
          <Btn small disabled={!!act.busy}
            onClick={() => act.post('reset', 'university_set', { code, overrides: { moved: null } }, () => 'Moved detection back to the manifest / global setting.')}>
            Reset to manifest
          </Btn>
        )}
      </div>
      <NoteLine note={act.note} />
      {lastDiff ? (
        <div className="flex flex-col gap-1.5">
          <div className="text-[12.5px] text-grayscale-1200">
            Last diff, run <RunLink ctx={ctx} id={rid} />:
            {num(lc.moved) !== null ? (
              <> <b>{fmtN(lc.moved)}</b> moved ({fmtN(lc.moved_changed ?? 0)} with content changes{n0(lc.moved_relaxed) > 0 ? `, ${fmtN(lc.moved_relaxed)} relaxed` : ''})</>
            ) : <span className="text-grayscale-1100"> no moved counts (the run predates moved detection)</span>}
            {notApplied > 0 && <> · <b>{fmtN(notApplied)}</b> possible move{notApplied === 1 ? '' : 's'} not applied</>}
            {str(st.moved_detection) && <span className="text-grayscale-1100"> · detection {movedDetectionText(st.moved_detection)}</span>}
          </div>
          {rejected.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {rejected.map(([k, v]) => (
                <Pill key={k} tone={k === 'low_similarity' ? 'amber' : 'grey'} title={k}>{MOVED_REJECT_LABEL[k] || k.replace(/_/g, ' ')} <b>{fmtN(v)}</b></Pill>
              ))}
            </div>
          )}
          {(notApplied > 0 || num(st.moved_would_match) !== null) && (
            <Collapse title={`Possible moves not applied in run #${rid} (${fmtN(notApplied)})`}>
              <MovedCandidates stats={st} />
            </Collapse>
          )}
        </div>
      ) : <div className="text-xs text-grayscale-1100">No run has diffed this university yet.</div>}
    </Section>
  );
};

const UploadBox: React.FC<{ code: string; data: any; onDone: (d: any) => void }> = ({ code, data, onDone }) => {
  const m = obj(data.manifest);
  const expected = arr(m.manual_inputs).map(str).filter(Boolean);
  const uploads = arr(data.uploads);
  const [file, setFile] = useState<{ name: string; size: number; b64: string } | null>(null);
  const [name, setName] = useState('');
  const [runAfter, setRunAfter] = useState(true);
  const [err, setErr] = useState('');
  const act = useAction();
  const pickFile = (f: File | undefined) => {
    setErr(''); setFile(null);
    if (!f) return;
    if (f.size > MAX_UPLOAD_MB * 1024 * 1024) { setErr(`${f.name} is larger than ${MAX_UPLOAD_MB} MB.`); return; }
    const reader = new FileReader();
    reader.onload = () => {
      setFile({ name: f.name, size: f.size, b64: String(reader.result || '') });
      // The mapper reads a fixed name: suggest it when the manifest gives one without wildcards.
      const fixed = expected.find(p => !/[*?[]/.test(p));
      setName(fixed && expected.length === 1 ? fixed : f.name);
    };
    reader.onerror = () => setErr('Could not read the file.');
    reader.readAsDataURL(f);
  };
  const upload = async () => {
    if (!file) return;
    const d = await act.post('upload', 'upload_source', { code, filename: name.trim() || file.name, b64: file.b64, run: runAfter },
      r => `Uploaded ${str(obj(r.upload).name)} (${fmtBytes(obj(r.upload).bytes)})${obj(r.upload).replaced ? ', the previous file was kept in _previous/' : ''}${r.run ? ` — run #${obj(r.run).id} queued` : ''}${str(r.run_error) ? ` — but the run was not started: ${str(r.run_error)}. Start it with Full run above.` : ''}.`);
    if (d) { setFile(null); onDone(d); }
  };
  return (
    <Section title="Manual source file">
      <div className="text-xs text-grayscale-1100">
        This university cannot be crawled: its technologies come from a file. Upload the new file; the mapper re-runs on it and the usual
        diff / enrich / approval follows. {expected.length ? <>Expected name{expected.length > 1 ? 's' : ''}: <b>{expected.join(', ')}</b>.</> : null}
        {' '}Up to {MAX_UPLOAD_MB} MB; the server checks the file really is what its extension says.
      </div>
      {uploads.length > 0 ? (
        <div className="flex flex-col gap-0.5 text-[12.5px]">
          {uploads.map((u, i) => (
            <div key={i} className="flex items-center gap-2">
              <code className="font-mono">{str(u.name)}</code>
              <span className="text-grayscale-1100">{fmtBytes(u.bytes)} · {fmtIST(u.modified_at)}</span>
            </div>
          ))}
        </div>
      ) : <div className="text-xs text-amber-800">No source file on the server yet — the first run needs one.</div>}
      <div className="flex items-center gap-2 flex-wrap">
        <input type="file" accept=".csv,.xlsx,.xls,.pdf,.json,.txt,.tsv" onChange={e => pickFile(e.target.files?.[0])}
          className="text-xs" />
        {file && (
          <>
            <span className="text-xs text-grayscale-1100">save as</span>
            <TextInput value={name} onChange={setName} />
            <label className="inline-flex items-center gap-1.5 text-xs"><input type="checkbox" checked={runAfter} onChange={e => setRunAfter(e.target.checked)} /> queue a full run after</label>
            <Btn kind="primary" small onClick={upload} disabled={!!act.busy}>{act.busy ? 'Uploading…' : `Upload ${fmtBytes(file.size)}`}</Btn>
          </>
        )}
      </div>
      {err && <Msg kind="error">{err}</Msg>}
      <NoteLine note={act.note} />
    </Section>
  );
};

const HEALTH_SERIES = [
  { key: 'ok', label: 'OK', color: '#2F9E5B' },
  { key: 's429', label: '429', color: '#D0443A' },
  { key: 's403', label: '403', color: '#B4462A' },
  { key: 'other4xx', label: 'other 4xx', color: '#D08A1E' },
  { key: 's5xx', label: '5xx', color: '#8B6BB8' },
  { key: 'neterr', label: 'timeouts / conn errors', color: '#6B6B6B' },
];

const HealthTrend: React.FC<{ health: any; ctx: RecrawlCtx }> = ({ health, ctx }) => {
  const runs = arr(obj(health).runs);
  const rows = runs.slice().reverse().map(r => ({
    x: `#${str(r.run_id)}`, ok: n0(r.ok), s429: n0(r.s429), s403: n0(r.s403),
    other4xx: Math.max(0, n0(r.s4xx) - n0(r.s403) - n0(r.s429)), s5xx: n0(r.s5xx), neterr: n0(r.timeouts) + n0(r.conn_errors), _r: r,
  }));
  if (!runs.length) {
    return <div className="text-sm text-grayscale-1100">No request telemetry yet — the governor records it on the first crawl.</div>;
  }
  return (
    <div className="flex flex-col gap-3">
      <StackedColumns rows={rows} xKey="x" series={HEALTH_SERIES} height={130} unit="requests"
        onPick={r => ctx.openRun(str(r.x).replace('#', ''))} />
      <div className="overflow-auto rounded-lg border border-grayscale-300">
        <table className="w-full text-[12.5px]">
          <thead className="bg-grayscale-200 text-[11px] text-grayscale-1200">
            <tr>{['Run', 'When', 'Requests', 'OK', '429', '403', '5xx', 'Timeouts', 'CF', 'Backoffs', 'p95', 'Workers', 'Interval'].map(h =>
              <th key={h} className="text-left px-2 py-1 whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {runs.map((r, i) => (
              <tr key={i} className="border-t border-grayscale-200">
                <td className="px-2 py-1"><RunLink ctx={ctx} id={r.run_id} /></td>
                <td className="px-2 py-1 whitespace-nowrap" title={fmtIST(r.at)}>{fmtAgo(r.at)}</td>
                <td className="px-2 py-1">{fmtN(r.requests)}</td>
                <td className="px-2 py-1">{fmtPct(r.ok, r.requests)}</td>
                <td className={`px-2 py-1 ${n0(r.s429) ? 'text-red-700 font-medium' : ''}`}>{fmtN(r.s429)}</td>
                <td className={`px-2 py-1 ${n0(r.s403) ? 'text-red-700' : ''}`}>{fmtN(r.s403)}</td>
                <td className="px-2 py-1">{fmtN(r.s5xx)}</td>
                <td className="px-2 py-1">{fmtN(n0(r.timeouts) + n0(r.conn_errors))}</td>
                <td className={`px-2 py-1 ${n0(r.cf_challenges) ? 'text-red-700' : ''}`}>{fmtN(r.cf_challenges)}</td>
                <td className="px-2 py-1">{fmtN(r.backoffs)}</td>
                <td className="px-2 py-1 whitespace-nowrap">{num(r.p95_ms) === null ? '—' : `${Math.round(n0(r.p95_ms)).toLocaleString()} ms`}</td>
                <td className="px-2 py-1">{fmtN(r.max_workers_used)}</td>
                <td className="px-2 py-1">{num(r.min_interval_used) === null ? '—' : `${r.min_interval_used} s`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// the drawer
// ---------------------------------------------------------------------------
const UniversityDrawer: React.FC<{ code: string; ctx: RecrawlCtx; onClose: () => void }> = ({ code, ctx, onClose }) => {
  const [localTick, setLocalTick] = useState(0);
  const res = useAdminGet<any>(S, 'university', { code }, [code, ctx.tick, localTick]);
  const d = obj(res.data);
  const u = obj(d.university);
  const m = obj(d.manifest);
  const eff = obj(d.effective);
  const ov = obj(d.overrides);
  const name = str(u.name || m.university_name || code);
  const saved = () => { setLocalTick(t => t + 1); ctx.reloadAll(); };
  const act = useAction(saved);
  const [startMode, setStartMode] = useState<RunMode | null>(null);
  const autoApprove = useAutoApprove();
  const uploadRef = useRef<HTMLDivElement | null>(null);
  const goUpload = () => uploadRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // The run dialog reads the university from the panel's list; this drawer's own copy is the fresher one.
  const uniCtx = useMemo(() => {
    if (!d.university) return ctx;
    const row = toUni(d.university);
    const map = new Map(ctx.uniByCode);
    map.set(row.code, row);
    return { ...ctx, uniByCode: map, reloadAll: saved };
  }, [ctx, d.university]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openRun = num(u.open_run_id);
  const blocked = obj(u.run_blocked);
  const manualNoFile = (u.kind === 'manual_file' || m.kind === 'manual_file') && u.has_upload === false;
  const blockReasons = Array.from(new Set(RUN_MODES.map(k => str(blocked[k])).filter(Boolean)));
  const projection = d.projection && typeof d.projection === 'object' ? d.projection : null;
  const openQueue = openRun === null ? '' : queueText(obj(arr(d.runs).find(r => num(r.id) === openRun)));
  const scrape = u.last_scrape && typeof u.last_scrape === 'object' ? obj(u.last_scrape) : null;
  const probe = toProbe(u.last_probe);
  const master = obj(u.master);
  const listed = n0(master.active) + n0(master.missing);
  const drift = num(u.drift);
  const risk = arr(u.risk).length ? arr(u.risk) : arr(m.risk);
  const steps = arr(m.steps);
  const timeline = arr(d.timeline);
  const runs = arr(d.runs);
  const subtitle = [code, u.kind === 'manual_file' ? 'manual source file' : str(u.kind || m.kind) || 'scraper', str(u.platform || m.platform),
    str(u.country || m.country), str(u.folder || m.folder)].filter(Boolean).join(' · ');

  return (
    <Drawer title={name} subtitle={subtitle} onClose={onClose}>
      {res.error && !res.data && <Msg kind="error">{res.error}</Msg>}
      {res.loading && !res.data ? <Loading label={`Loading ${code}…`} /> : res.data && (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            {u.enabled === false && <Pill tone="grey">disabled</Pill>}
            {u.paused && <Pill tone="amber">paused</Pill>}
            {u.needs_attention && <Pill tone="red" title={str(u.due_reason)}>needs attention</Pill>}
            {u.schedule?.enabled ? <Pill tone="navy">{scheduleText(u.schedule)}</Pill> : <Pill tone="grey">not scheduled</Pill>}
            {openRun !== null && <span className="text-xs text-blue-800">open run <RunLink ctx={ctx} id={openRun} /></span>}
            {u.portal_url && <a href={str(u.portal_url)} target="_blank" rel="noreferrer noopener" className="text-xs text-primary-700 underline underline-offset-2 ml-auto">portal ↗</a>}
          </div>
          <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs text-grayscale-1200">
            {scrape ? (
              <span className="inline-flex items-center gap-1.5 flex-wrap"
                title={`${fmtIST(scrape.finished_at || scrape.started_at || scrape.created_at)}${scrapeResults(scrape) ? `\n${scrapeResults(scrape)}` : ''}`}>
                <span className="text-grayscale-1100">latest {MODE_LABEL[str(scrape.mode)] || str(scrape.mode)}</span>
                <RunLink ctx={ctx} id={scrape.id} /> <StatusPill status={scrape.status} />
                <span>{scrapeResults(scrape) || (scrape.status === 'running' || scrape.status === 'queued' ? 'no results yet' : 'no results')}</span>
                <span className="text-grayscale-1100">· {fmtAgo(scrape.finished_at || scrape.started_at || scrape.created_at)}</span>
              </span>
            ) : <span className="text-grayscale-1100">No dry or full run yet</span>}
            {probe ? <ProbeBadge probe={probe} ctx={ctx} className="text-grayscale-1100" />
              : u.kind !== 'manual_file' && <span className="text-grayscale-1100">not probed yet</span>}
          </div>
          {u.note && <div className="text-xs rounded-lg bg-amber-50 border border-amber-200 px-3 py-1.5 text-amber-900">Note: {str(u.note)}</div>}

          <div className="rounded-xl border border-primary-300 bg-white p-3 flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-bold text-grayscale-1300 font-grotesk mr-1">Start a run</span>
              {RUN_MODES.map(k => (
                <Btn key={k} small kind={k === 'full' ? 'primary' : 'subtle'} disabled={!!blocked[k]}
                  title={str(blocked[k]) || RUN_MODE_INFO[k].short} onClick={() => setStartMode(k)}>
                  {RUN_MODE_INFO[k].label}
                </Btn>
              ))}
              {manualNoFile && <Btn small kind="primary" onClick={goUpload}>Upload the source file</Btn>}
              <span className="text-[11px] text-grayscale-1100">You confirm before anything starts.</span>
            </div>
            <div className="text-xs text-grayscale-1200 flex flex-col gap-0.5">
              <span><b>Probe</b> — {RUN_MODE_INFO.probe_only.short.toLowerCase()} · <b>Dry run</b> — {RUN_MODE_INFO.dry_run.short.toLowerCase()} ·
                {' '}<b>Full run</b> — {RUN_MODE_INFO.full.short.toLowerCase()}.</span>
              {openRun !== null && (
                <span className="text-blue-800">Run <RunLink ctx={ctx} id={openRun} /> is open
                  {u.open_run_status ? ` (${[str(u.open_run_status).replace(/_/g, ' '), openQueue].filter(Boolean).join(', ')})` : ''}:
                  a new run can start once it is finished, approved, rejected or cancelled.</span>
              )}
              {openRun === null && blockReasons.map(r => <span key={r} className="text-amber-800">{r}</span>)}
            </div>
          </div>
          {startMode && <RunStartModal ctx={uniCtx} code={code} mode={startMode} onClose={() => setStartMode(null)} onUpload={goUpload} />}

          <div className="flex gap-2 flex-wrap items-center">
            <Btn small disabled={!!act.busy}
              onClick={() => act.post('pause', 'university_set', { code, overrides: { paused: !u.paused } }, () => (u.paused ? 'Resumed: the scheduler may start it again.' : 'Paused: the scheduler will not start it.'))}>
              {u.paused ? 'Resume schedule' : 'Pause'}
            </Btn>
            {ov.needs_attention && (
              <Btn small disabled={!!act.busy} onClick={() => act.post('attn', 'university_set', { code, overrides: { needs_attention: null } }, () => 'Attention flag cleared.')}>
                Clear attention flag
              </Btn>
            )}
            <span className="flex-1" />
            <Btn small onClick={() => ctx.goFlow(code)}>Pipeline flow →</Btn>
            <Btn small onClick={() => ctx.goChanges({ code })}>Changes →</Btn>
          </div>
          <NoteLine note={act.note} onClose={() => act.setNote(null)} />

          <div className="flex gap-2 flex-wrap">
            <Tile label="Listed (master)" value={fmtN(listed)} hint={`${fmtN(master.active)} active · ${fmtN(master.missing)} missing`} />
            <Tile label="Removed" value={fmtN(master.removed)} hint="kept in the master history" />
            <Tile label="Live rows" value={fmtN(u.live_count)}
              hint={drift === null ? '' : `drift ${drift > 0 ? '+' : ''}${drift.toLocaleString()}`}
              tone={drift !== null && listed > 0 && Math.abs(drift) > Math.max(5, 0.02 * listed) ? 'red' : undefined}
              title="Live technologies of this university vs master active + missing" />
            <Tile label="Last success" value={u.last_success_at ? fmtAgo(u.last_success_at) : 'never'} hint={u.last_success_at ? fmtIST(u.last_success_at) : 'no engine run yet'} />
            <Tile label="Next due" value={u.schedule?.enabled && u.next_due_at ? fmtAgo(u.next_due_at) : '—'} hint={str(u.due_reason)} />
            <Tile label="Failures in a row" value={fmtN(u.consecutive_failures ?? 0)} tone={n0(u.consecutive_failures) >= 2 ? 'red' : undefined} />
          </div>

          {projection && (
            <ProjectionBox p={projection} ctx={ctx} showRun
              title={projection.approvable ? 'If approved' : 'If these results were approved'}
              note={!projection.approvable ? (
                <div className="text-xs text-grayscale-1100">A dry run changes nothing. {fullRunApprovalText(autoApprove)}</div>
              ) : undefined} />
          )}

          <Section title="Manifest">
            <Facts>
              <Fact label="DB name">{str(m.university_name) || '—'}</Fact>
              <Fact label="Aliases">{arr(m.aliases).length ? arr(m.aliases).join(', ') : '—'}</Fact>
              <Fact label="Folder">{str(m.folder) || '—'}</Fact>
              <Fact label="Mapped file">{str(m.mapped_file) || '—'}</Fact>
              <Fact label="Details file">{str(m.details_file) || '—'}</Fact>
              <Fact label="Change key">{str(m.change_key) || 'university_technology_url'}</Fact>
              <Fact label="Held below ratio">{num(eff.expected_min_ratio) ?? '—'}</Fact>
              <Fact label="Needs">{[...arr(m.needs), ...arr(m.system_needs)].join(', ') || '—'}</Fact>
              {arr(m.cache_dirs).length > 0 && <Fact label="Persistent caches">{arr(m.cache_dirs).join(', ')}</Fact>}
              {arr(m.manual_inputs).length > 0 && <Fact label="Manual inputs">{arr(m.manual_inputs).join(', ')}</Fact>}
              {arr(m.modified_hint_fields).length > 0 && <Fact label="Last-modified fields">{arr(m.modified_hint_fields).join(', ')}</Fact>}
              {m.probe_url && <Fact label="Probe checks" title="The page the probe checks instead of the portal">{str(m.probe_url)}</Fact>}
              {m.probe_samples === false && <Fact label="Probe sample pages" title="The probe does not request sample technology pages">not checked</Fact>}
            </Facts>
            {risk.length > 0 && (
              <div className="flex flex-col gap-1">
                <div className="flex gap-1 flex-wrap">{risk.map(r => <Pill key={str(r)} tone="amber">⚑ {str(r).replace(/_/g, ' ')}</Pill>)}</div>
                {m.risk_notes && <div className="text-xs text-grayscale-1200 whitespace-pre-line">{str(m.risk_notes)}</div>}
              </div>
            )}
            {m.notes && <div className="text-xs text-grayscale-1200 whitespace-pre-line">{str(m.notes)}</div>}
            <div className="overflow-auto rounded-lg border border-grayscale-300">
              <table className="w-full text-[12.5px]">
                <thead className="bg-grayscale-200 text-[11px] text-grayscale-1200">
                  <tr>{['#', 'Step', 'Script', 'Outputs', 'Timeout', 'Re-runs', 'Note'].map(h => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {steps.map((s, i) => (
                    <tr key={i} className="border-t border-grayscale-200 align-top">
                      <td className="px-2 py-1 text-grayscale-1100">{i + 1}</td>
                      <td className="px-2 py-1 font-medium">{str(s.id)}</td>
                      <td className="px-2 py-1"><code className="font-mono">{str(s.script)}</code></td>
                      <td className="px-2 py-1 text-grayscale-1200">{arr(s.outputs).join(', ') || '—'}</td>
                      <td className="px-2 py-1 whitespace-nowrap">{num(s.timeout_min) === null ? '—' : `${s.timeout_min} min`}</td>
                      <td className="px-2 py-1">{num(s.retry_runs) ?? '—'}</td>
                      <td className="px-2 py-1 text-grayscale-1100">{str(s.note) || '—'}</td>
                    </tr>
                  ))}
                  {!steps.length && <tr><td colSpan={7} className="px-2 py-2 text-grayscale-1100">No scraper steps in the manifest.</td></tr>}
                </tbody>
              </table>
            </div>
            <Collapse title="Full manifest (JSON)"><JsonBox value={m} maxH="24rem" /></Collapse>
          </Section>

          <ScheduleEditor key={`sc-${JSON.stringify(u.schedule)}`} code={code} uni={u} manifest={m} overrides={ov} onSaved={saved} />
          <ThrottleEditor key={`th-${JSON.stringify(obj(d.throttle).effective)}`} code={code} data={d} health={d.health} onSaved={saved} />
          {u.kind === 'manual_file' || m.kind === 'manual_file' ? <div ref={uploadRef}><UploadBox code={code} data={d} onDone={saved} /></div> : null}

          <Section title="Changes over the last 12 months"
            right={<Btn small onClick={() => ctx.goChanges({ code })}>Full timeline →</Btn>}>
            <StackedColumns rows={timeline} xKey="bucket" series={EVENT_SERIES} height={140}
              xLabel={(x, long) => bucketLabel(x, 'month', long)} initiallyOff={['missing', 'manual_fix']}
              empty="No master events for this university yet." />
          </Section>
          <UniversityChangeHeatmap code={code} ctx={ctx} />

          <Section title={`Runs (${runs.length})`} right={<Btn small onClick={() => ctx.goRuns({ code })}>All runs →</Btn>}>
            <RunTable runs={runs} ctx={ctx} showUni={false} maxHeight="20rem" empty="No engine run yet for this university." />
          </Section>

          <Section title="Request health (per run)">
            <HealthTrend health={d.health} ctx={ctx} />
          </Section>

          <GuardEditor key={`g-${JSON.stringify([eff.enabled, eff.always_approve, eff.expected_min_ratio, ov.remove_after_misses, ov.note])}`}
            code={code} data={d} onSaved={saved} />
          <CleanupEditor key={`c-${JSON.stringify(eff.cleanup)}`} code={code} data={d} onSaved={saved} />
          <MovedEditor key={`mv-${JSON.stringify([ov.moved, eff._moved])}`} code={code} data={d} ctx={ctx} onSaved={saved} />

          <Section title="Admin overrides" right={Object.keys(ov).length ? (
            <DangerBtn small onConfirm={() => act.post('resetall', 'university_set', { code, overrides: {}, replace: true }, () => 'Every override removed: the manifest applies again.')}>
              Remove all overrides
            </DangerBtn>
          ) : undefined}>
            <div className="text-xs text-grayscale-1100">
              Stored in recrawl.university_settings and merged over the manifest at run time (the manifest file is the code default).
            </div>
            {Object.keys(ov).length ? <JsonBox value={ov} /> : <div className="text-sm text-grayscale-1100">None — the manifest applies as written.</div>}
          </Section>
        </>
      )}
    </Drawer>
  );
};

export default UniversityDrawer;
